import { useEffect, useState } from 'react'
import { adminClient, type AdminMetrics } from '../../services/adminClient'
import { ApiClientError } from '../../services/apiClient'

/**
 * Dashboard del portal admin (§12.3 SRS).
 * Tarjetas con métricas clave:
 *   - Total de usuarios y tenants
 *   - Reportes generados (última semana / último mes)
 *   - Imágenes procesadas (última semana / último mes)
 *   - Mejoras IA del mes actual + costo USD acumulado
 */
export function AdminDashboardPage() {
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null)
  const [generatedAt, setGeneratedAt] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    adminClient
      .dashboard()
      .then((res) => {
        if (cancelled) return
        setMetrics(res.metrics)
        setGeneratedAt(res.generated_at)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar el dashboard.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="content-stack">
      <header className="list-toolbar">
        <div className="list-toolbar__title">
          <h1>Dashboard</h1>
          {generatedAt && (
            <span className="muted">Actualizado {new Date(generatedAt).toLocaleString('es-MX')}</span>
          )}
        </div>
      </header>

      {error && <p className="error">{error}</p>}
      {loading && <p className="muted">Cargando métricas…</p>}

      {metrics && (
        <>
          <div className="metrics-grid">
            <MetricCard label="Usuarios activos" value={metrics.total_users} />
            <MetricCard label="Tenants" value={metrics.total_tenants} />
            <MetricCard label="Reportes · 7d" value={metrics.reports_last_week} />
            <MetricCard label="Reportes · 30d" value={metrics.reports_last_month} />
            <MetricCard label="Imágenes · 7d" value={metrics.images_last_week} />
            <MetricCard label="Imágenes · 30d" value={metrics.images_last_month} />
          </div>

          <section className="card">
            <h3>Uso del mes</h3>
            <dl>
              <dt>Mejoras de redacción exitosas</dt>
              <dd className="mono">{metrics.ai_enhancements_this_month}</dd>
              <dt>Tokens de entrada</dt>
              <dd className="mono">{metrics.ai_tokens_in_this_month.toLocaleString('es-MX')}</dd>
              <dt>Tokens de salida</dt>
              <dd className="mono">{metrics.ai_tokens_out_this_month.toLocaleString('es-MX')}</dd>
              <dt>Costo estimado IA (USD)</dt>
              <dd className="mono">
                ${metrics.ai_cost_this_month_usd.toLocaleString('en-US', {
                  minimumFractionDigits: 4,
                  maximumFractionDigits: 4,
                })}
              </dd>
            </dl>
          </section>
        </>
      )}
    </div>
  )
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="metric-card">
      <span className="metric-card__label">{label}</span>
      <strong className="metric-card__value">{value.toLocaleString('es-MX')}</strong>
    </div>
  )
}
